import { useReducedMotion } from 'motion/react';
import { useState } from 'react';
import type { BaseReactPlayerProps } from 'react-player/base';
import type { VimeoPlayerProps } from 'react-player/vimeo';

import Icon, { IconName } from '@/components/Icon/Icon';
import ArtDirectedImage, {
  type ImageSources,
} from '@/components/Image/ArtDirectedImage';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import type { Contentful } from '@/contentful/contentful-exports';
import { brandColors } from '@/styles/theme/values/brandColors';
import { getVideoAspectRatio } from '@/utils/vimeo';

import useStyles from './VimeoVideo.styles';

interface Props extends VimeoPlayerProps {
  thumbnailSources?: ImageSources;
  containerClasses?: string;
  color?: string;
  background?: boolean;
  isRounded?: boolean;
}

export interface VimeoVideoType extends Contentful {
  fields: {
    name: string;
    videoUrl: string;
    autoPlay: boolean;
  };
}

const autoplayProps: BaseReactPlayerProps = {
  playing: true,
  loop: true,
  playsinline: true,
  muted: true,
  controls: false,
};

export default function VimeoVideo({
  thumbnailSources,
  containerClasses,
  color = brandColors.chargeBlue,
  isRounded,
  autoplay,
  ...rest
}: Props) {
  const prefersReducedMotion = useReducedMotion();
  const [userInitiatedPlay, setUserInitiatedPlay] = useState(false);
  const [hideThumbnail, setHideThumbnail] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<string | null>(null);
  const { classes, cx } = useStyles({ aspectRatio });

  // Show static thumbnail with play button for reduced motion users when autoplay is enabled
  const shouldShowPlayOverlay =
    prefersReducedMotion && autoplay && thumbnailSources && !userInitiatedPlay;

  const handleError: VimeoPlayerProps['onError'] = (error) => {
    rest?.onError?.(error);
    if (!error) return;

    if (error.name === 'NotAllowedError' && error.method === 'play') {
      setHideThumbnail(false);
    }
  };

  const handlePlay = () => {
    rest.onPlay?.();
    setHideThumbnail(true);
  };

  const handleReady: VimeoPlayerProps['onReady'] = async (player) => {
    rest.onReady?.(player);
    const videoElement = player.getInternalPlayer();
    const aspectRatio = await getVideoAspectRatio(videoElement);
    setAspectRatio(aspectRatio);

    if (autoplay) {
      // if autoplay playback is on, let onPlay callback hide the thumbnail.
      // this results in a smooth picture to video playback transition.
      return;
    } else {
      // otherwise, hide the thumbnail as soon as the player loads
      // and let the user control the playback
      setHideThumbnail(true);
    }
  };

  return (
    <div
      className={cx(
        classes.container,
        isRounded && classes.rounded,
        containerClasses
      )}
    >
      {thumbnailSources && (
        <ArtDirectedImage
          images={thumbnailSources}
          role='presentation'
          alt=''
          className={cx(classes.absoluteFill, classes.thumbnail, {
            [classes.hidden]: hideThumbnail && !shouldShowPlayOverlay,
          })}
        />
      )}

      {/* Play button overlay for reduced motion users */}
      {shouldShowPlayOverlay && (
        <button
          type='button'
          className={classes.playButtonOverlay}
          onClick={() => {
            setUserInitiatedPlay(true);
            setHideThumbnail(true);
          }}
          aria-label='Play video'
        >
          <Icon
            name={IconName.PLAY}
            width={60}
            height={60}
            className={classes.playIcon}
          />
        </button>
      )}

      {/* Only render video player if not showing static overlay, or if user clicked play */}
      {(!shouldShowPlayOverlay || userInitiatedPlay) && (
        // biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome
        <VideoPlayer
          id='vimeo-player'
          data-testid='video-player'
          width='100%'
          height='100%'
          onError={handleError}
          onPlay={handlePlay}
          onReady={handleReady}
          className={classes.absoluteFill}
          config={{ playerOptions: { color: color.substring(1) } }}
          {...(autoplay && !prefersReducedMotion ? autoplayProps : {})}
          {...(userInitiatedPlay ? { playing: true } : {})}
          {...rest}
        />
      )}
    </div>
  );
}
